// AdminBlogPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, storage } from '../../firebase/firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
// Replaced lucide-react icons with Unicode symbols for React 19 compatibility
import './BlogPage.css';

const AdminBlogPage = () => {
  // State for blog posts list
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for form inputs
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [keywords, setKeywords] = useState('');
  const [author, setAuthor] = useState('CarConsul Team');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // State for edit mode
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Refs
  const fileInputRef = useRef(null);
  
  // Fetch all blog posts
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const blogCollection = collection(db, 'blogposts');
        const blogQuery = query(blogCollection, orderBy('createdAt', 'desc'));
        const blogSnapshot = await getDocs(blogQuery);
        const blogList = blogSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setBlogPosts(blogList);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setError('Failed to load blog posts');
        setIsLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);
  
  // Format date to readable format
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };
  
  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with a single hyphen
      .trim();
  };
  
  // Handle title change and auto-generate slug
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    
    // Only auto-generate slug if we're not in edit mode or the slug hasn't been manually edited
    if (!editMode || slug === generateSlug(title)) {
      setSlug(generateSlug(newTitle));
    }
  };
  
  // Handle image selection
  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
    }
  };
  
  // Upload image to Firebase Storage
  const uploadImage = async () => {
    if (!selectedImage) return null;
    
    try {
      const storageRef = ref(storage, `blog/${Date.now()}_${selectedImage.name}`);
      const uploadTask = uploadBytesResumable(storageRef, selectedImage);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Error uploading image:', error);
            reject(error);
          },
          async () => {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            setImageUrl(downloadUrl);
            resolve(downloadUrl);
          }
        );
      });
    } catch (error) {
      console.error('Error starting upload:', error);
      return null;
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Check required fields
      if (!title || !slug || !htmlContent) {
        alert('Please fill in all required fields: title, slug, and content');
        return;
      }
      
      // Upload image if selected
      let finalImageUrl = imageUrl;
      if (selectedImage) {
        finalImageUrl = await uploadImage();
        if (!finalImageUrl) {
          alert('Failed to upload image. Please try again.');
          return;
        }
      }
      
      if (editMode && editId) {
        // Update existing blog post
        const blogPostRef = doc(db, 'blogposts', editId);
        await updateDoc(blogPostRef, {
          title,
          slug,
          htmlContent,
          excerpt,
          keywords,
          author,
          imageUrl: finalImageUrl,
          updatedAt: serverTimestamp()
        });
        
        alert('Blog post updated successfully!');
      } else {
        // Create new blog post
        await addDoc(collection(db, 'blogposts'), {
          title,
          slug,
          htmlContent,
          excerpt,
          keywords,
          author,
          imageUrl: finalImageUrl,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        alert('Blog post created successfully!');
      }
      
      // Reset form
      resetForm();
      
      // Refresh blog posts list
      const blogCollection = collection(db, 'blogposts');
      const blogQuery = query(blogCollection, orderBy('createdAt', 'desc'));
      const blogSnapshot = await getDocs(blogQuery);
      const blogList = blogSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBlogPosts(blogList);
      
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save blog post: ' + error.message);
    }
  };
  
  // Handle edit button click
  const handleEdit = (post) => {
    setEditMode(true);
    setEditId(post.id);
    setTitle(post.title);
    setSlug(post.slug);
    setHtmlContent(post.htmlContent);
    setExcerpt(post.excerpt || '');
    setKeywords(post.keywords || '');
    setAuthor(post.author || 'PermitZen Team');
    setImageUrl(post.imageUrl || '');
    setSelectedImage(null);
    setUploadProgress(0);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle delete button click
  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Delete the document from Firestore
      await deleteDoc(doc(db, 'blogposts', id));
      
      // Delete the image from Storage if it exists
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (imageError) {
          console.error('Error deleting image:', imageError);
          // Continue even if image deletion fails
        }
      }
      
      // Update the UI
      setBlogPosts(blogPosts.filter(post => post.id !== id));
      alert('Blog post deleted successfully!');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post: ' + error.message);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setEditMode(false);
    setEditId(null);
    setTitle('');
    setSlug('');
    setHtmlContent('');
    setExcerpt('');
    setKeywords('');
    setAuthor('CarConsul Team');
    setSelectedImage(null);
    setImageUrl('');
    setUploadProgress(0);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1929] text-white">
      <Helmet>
        <title>Blog Admin | CarConsul</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[#BCE6EB]">Blog Administration</h1>
          <Link to="/blog" className="bg-[#1B4965] text-[#BCE6EB] px-4 py-2 rounded-lg hover:bg-opacity-80 transition-colors">
            View Blog
          </Link>
        </div>

        {/* Blog Post Form */}
        <div className="bg-[#1B4965] bg-opacity-20 rounded-xl p-6 border border-[#62B6CB] border-opacity-20 mb-10">
          <h2 className="text-xl font-bold text-[#BCE6EB] mb-4">
            {editMode ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[#BCE6EB] mb-1">Title*</label>
              <input
                type="text"
                value={title}
                onChange={handleTitleChange}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Slug* (URL path)</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white"
                required
              />
              <p className="text-sm text-[#BCE6EB] text-opacity-60 mt-1">
                Will be accessible at: https://carconsul.com/blog/{slug}
              </p>
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Content* (HTML)</label>
              <textarea
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white font-mono h-64"
                required
              />
              <p className="text-sm text-[#BCE6EB] text-opacity-60 mt-1">
                Enter HTML content for the blog post.
              </p>
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Excerpt/Summary</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white h-20"
              />
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Keywords</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white"
                placeholder="building permits, home renovation, zoning, etc."
              />
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Author</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0A1929] border border-[#62B6CB] border-opacity-30 text-white"
              />
            </div>
            
            <div>
              <label className="block text-[#BCE6EB] mb-1">Featured Image</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="bg-[#1B4965] text-[#BCE6EB] px-4 py-2 rounded-lg hover:bg-opacity-80 transition-colors flex items-center"
                >
                  📷
                  Choose Image
                </button>
                {selectedImage && (
                  <span className="text-[#BCE6EB]">{selectedImage.name}</span>
                )}
                {imageUrl && !selectedImage && (
                  <div className="relative">
                    <img src={imageUrl} alt="Current" className="h-12 w-auto rounded" />
                    <button
                      type="button"
                      onClick={() => setImageUrl('')}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                    >
                      ❌
                    </button>
                  </div>
                )}
              </div>
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2">
                  <div className="w-full bg-[#0A1929] rounded-full h-2.5">
                    <div
                      className="bg-[#62B6CB] h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-[#BCE6EB] mt-1">Uploading: {Math.round(uploadProgress)}%</p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                className="bg-[#62B6CB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors flex items-center"
              >
                💾
                {editMode ? 'Update Blog Post' : 'Create Blog Post'}
              </button>
              {editMode && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Blog Posts List */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-[#BCE6EB] mb-4">Manage Blog Posts</h2>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#62B6CB]"></div>
            </div>
          ) : error ? (
            <div className="bg-[#1B4965] bg-opacity-10 rounded-xl p-6 border border-red-400 border-opacity-30 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <p className="text-[#BCE6EB] text-opacity-70">
                Please try again later.
              </p>
            </div>
          ) : blogPosts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-[#1B4965] bg-opacity-10 rounded-xl overflow-hidden">
                <thead className="bg-[#1B4965] bg-opacity-30">
                  <tr>
                    <th className="py-3 px-4 text-left text-[#BCE6EB]">Title</th>
                    <th className="py-3 px-4 text-left text-[#BCE6EB]">Date</th>
                    <th className="py-3 px-4 text-left text-[#BCE6EB]">Author</th>
                    <th className="py-3 px-4 text-left text-[#BCE6EB]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogPosts.map(post => (
                    <tr key={post.id} className="border-t border-[#62B6CB] border-opacity-20">
                      <td className="py-3 px-4 text-[#BCE6EB]">{post.title}</td>
                      <td className="py-3 px-4 text-[#BCE6EB] text-opacity-70">
                        {formatDate(post.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-[#BCE6EB] text-opacity-70">
                        {post.author || 'PermitZen Team'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <a
                            href={`/blog/${post.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#1B4965] text-[#BCE6EB] p-2 rounded-lg hover:bg-opacity-80 transition-colors"
                            title="View"
                          >
                            👁️
                          </a>
                          <button
                            onClick={() => handleEdit(post)}
                            className="bg-[#62B6CB] text-white p-2 rounded-lg hover:bg-opacity-80 transition-colors"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(post.id, post.imageUrl)}
                            className="bg-red-500 text-white p-2 rounded-lg hover:bg-opacity-80 transition-colors"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-[#1B4965] bg-opacity-10 rounded-xl p-6 border border-[#62B6CB] border-opacity-20 text-center">
              <p className="text-[#BCE6EB] text-opacity-70">
                No blog posts available yet. Create your first post above!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlogPage;
